import { useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion, AnimatePresence } from 'framer-motion'
import { useGlobalContext } from '../context/GlobalContext'
import Button from '@comp/ui/button'

interface Props {
  isOpen: boolean
  onClose: () => void
}

const labels: Record<string, { en: string; tr: string }> = {
  // Section titles
  'Solutions': { en: 'Solutions', tr: 'Çözümler' },
  'Integrations': { en: 'Integrations', tr: 'Entegrasyonlar' },
  'Pricing': { en: 'Pricing', tr: 'Fiyatlandırma' },
  'For you': { en: 'For you', tr: 'Sizin için' },
  'Learn': { en: 'Learn', tr: 'Öğrenin' },
  'Industries': { en: 'Industries', tr: 'Sektörler' },
  'Features': { en: 'Features', tr: 'Özellikler' },
  'Resources': { en: 'Resources', tr: 'Kaynaklar' },
  'Company': { en: 'Company', tr: 'Şirket' },
  // Links
  'Overview': { en: 'Overview', tr: 'Genel bakış' },
  'Single Sign-On': { en: 'Single Sign-On', tr: 'Tek Oturum Açma' },
  'Multi-Factor Authentication': { en: 'Multi-Factor Authentication', tr: 'Çok Faktörlü Kimlik Doğrulama' },
  'Monofor Identity': { en: 'Monofor Identity', tr: 'Monofor Identity' },
  'Directory & sources': { en: 'Directory & sources', tr: 'Dizin ve kaynaklar' },
  'User lifecycle': { en: 'User lifecycle', tr: 'Kullanıcı yaşam döngüsü' },
  'Identity automation': { en: 'Identity automation', tr: 'Kimlik otomasyonu' },
  'ITDR': { en: 'ITDR', tr: 'ITDR' },
  'AI agent identity': { en: 'AI agent identity', tr: 'AI ajan kimliği' },
  'Reporting': { en: 'Reporting', tr: 'Raporlama' },
  'Security Score': { en: 'Security Score', tr: 'Güvenlik Skoru' },
  'Identity intelligence': { en: 'Identity Intelligence (IVIP)', tr: 'Kimlik İstihbaratı (IVIP)' },
  'MCP Gateway': { en: 'MCP Gateway', tr: 'MCP Gateway' },
  'Gateway': { en: 'Gateway', tr: 'Gateway' },
  'Credential vault': { en: 'Credential vault', tr: 'Kimlik bilgisi kasası' },
  'Session recording': { en: 'Session recording', tr: 'Oturum kaydı' },
  'Just-in-time access': { en: 'Just-in-time access', tr: 'Tam zamanında erişim' },
  'Certificate Manager': { en: 'Certificate Manager', tr: 'Sertifika Yöneticisi' },
  'Kubernetes & OpenShift': { en: 'Kubernetes & OpenShift', tr: 'Kubernetes ve OpenShift' },
  'Database access': { en: 'Database access', tr: 'Veritabanı erişimi' },
  'Endpoint Privilege Management': { en: 'Endpoint Privilege Management', tr: 'Uç Nokta Ayrıcalık Yönetimi' },
  'Secrets management': { en: 'Secrets management', tr: 'Gizli bilgi yönetimi' },
  'Linux identity agent': { en: 'Linux identity agent', tr: 'Linux kimlik ajanı' },
  'AD bridging': { en: 'AD bridging', tr: 'AD bridging' },
  'Vendor access (VPAM)': { en: 'Vendor access (VPAM)', tr: 'Tedarikçi erişimi (VPAM)' },
  'Connectors': { en: 'Connectors', tr: 'Konnektörler' },
  'API': { en: 'API', tr: 'API' },
  'Governance': { en: 'Governance', tr: 'Yönetişim' },
  'AI agent security': { en: 'AI agent security', tr: 'AI ajan güvenliği' },
  'Machine identity': { en: 'Machine identity', tr: 'Makine kimliği' },
  'Identity security posture': { en: 'Identity security posture', tr: 'Kimlik güvenliği duruşu' },
  'DevOps privileged access': { en: 'DevOps privileged access', tr: 'DevOps ayrıcalıklı erişimi' },
  'Yerli kimlik yönetimi': { en: 'Yerli kimlik yönetimi', tr: 'Yerli kimlik yönetimi' },
  'Kamu için kimlik yönetimi': { en: 'Kamu için kimlik yönetimi', tr: 'Kamu için kimlik yönetimi' },
  'BDDK denetimine hazır erişim': { en: 'BDDK denetimine hazır erişim', tr: 'BDDK denetimine hazır erişim' },
  'Zero Trust': { en: 'Zero Trust', tr: 'Sıfır Güven' },
  'Joiner / Mover / Leaver': { en: 'Joiner / Mover / Leaver', tr: 'İşe Giriş / Geçiş / Ayrılış' },
  'Passwordless & MFA': { en: 'Passwordless & MFA', tr: 'Parolasız ve MFA' },
  'Audit & compliance': { en: 'Audit & compliance', tr: 'Denetim ve uyum' },
  'External & partner access': { en: 'External & partner access', tr: 'Dış ve iş ortağı erişimi' },
  'KVKK compliance': { en: 'KVKK compliance', tr: 'KVKK uyumu' },
  'DORA compliance': { en: 'DORA compliance', tr: 'DORA uyumu' },
  'GDPR compliance': { en: 'GDPR compliance', tr: 'GDPR uyumu' },
  'NIS2 compliance': { en: 'NIS2 compliance', tr: 'NIS2 uyumu' },
  'IEC 62443 compliance': { en: 'IEC 62443 compliance', tr: 'IEC 62443 uyumu' },
  '7545 Siber Güvenlik Kanunu': { en: '7545 Siber Güvenlik Kanunu', tr: '7545 Siber Güvenlik Kanunu' },
  'Browse the catalog': { en: 'Browse the catalog', tr: 'Kataloğa göz atın' },
  'Plans & pricing': { en: 'Plans & pricing', tr: 'Planlar ve fiyatlandırma' },
  'IT admins': { en: 'IT admins', tr: 'BT yöneticileri' },
  'Security engineers': { en: 'Security engineers', tr: 'Güvenlik mühendisleri' },
  'Developers': { en: 'Developers', tr: 'Geliştiriciler' },
  'Compliance officers': { en: 'Compliance officers', tr: 'Uyum sorumluları' },
  'End users': { en: 'End users', tr: 'Son kullanıcılar' },
  'All how-tos': { en: 'All how-tos', tr: 'Tüm rehberler' },
  'SSO into AWS': { en: 'SSO into AWS', tr: "AWS'ye SSO" },
  'MFA on VPN': { en: 'MFA on VPN', tr: "VPN'de MFA" },
  'Windows passwordless': { en: 'Windows passwordless', tr: 'Windows parolasız giriş' },
  'REST API getting started': { en: 'REST API getting started', tr: "REST API'ye başlangıç" },
  'Active Directory sync': { en: 'Active Directory sync', tr: 'Active Directory senkronizasyonu' },
  'Audit logs → SIEM': { en: 'Audit logs → SIEM', tr: 'Denetim logları → SIEM' },
  'Migrate from ADFS': { en: 'Migrate from ADFS', tr: "ADFS'ten geçiş" },
  'Migrate from Okta': { en: 'Migrate from Okta', tr: "Okta'dan geçiş" },
  'Financial Services': { en: 'Financial Services', tr: 'Finansal Hizmetler' },
  'Healthcare': { en: 'Healthcare', tr: 'Sağlık' },
  'Manufacturing': { en: 'Manufacturing', tr: 'Üretim' },
  'Public Sector': { en: 'Public Sector', tr: 'Kamu' },
  'Retail': { en: 'Retail', tr: 'Perakende' },
  'Telecommunications': { en: 'Telecommunications', tr: 'Telekomünikasyon' },
  'Blog': { en: 'Blog', tr: 'Blog' },
  'Glossary': { en: 'Glossary', tr: 'Sözlük' },
  'PAM TCO Calculator': { en: 'PAM TCO Calculator', tr: 'PAM TCO Hesaplayıcı' },
  'Compliance Checklist': { en: 'Compliance Checklist', tr: 'Uyum Kontrol Listesi' },
  'AD Account Scanner': { en: 'AD Account Scanner', tr: 'AD Hesap Tarayıcı' },
  'Documentation': { en: 'Documentation', tr: 'Dokümantasyon' },
  'Press kit': { en: 'Press kit', tr: 'Basın kiti' },
  'About': { en: 'About', tr: 'Hakkımızda' },
  'Become a Partner': { en: 'Become a Partner', tr: 'İş Ortağı Olun' },
  'Contact': { en: 'Contact', tr: 'İletişim' },
  // Bottom buttons
  'Start free trial →': { en: 'Start free trial →', tr: 'Ücretsiz deneme başlat →' },
  'Talk with our experts': { en: 'Talk with our experts', tr: 'Uzmanlarımızla görüşün' },
}

export default function SiteMobileNav({ isOpen, onClose }: Props) {
  const { globalData } = useGlobalContext()
  const features = globalData?.features ?? []
  const { locale } = useRouter()
  const lang = locale === 'tr' ? 'tr' : 'en'
  const t = (key: string) => labels[key]?.[lang] ?? key

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = original
      }
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 top-16 z-40 overflow-y-auto bg-surface xl:hidden"
        >
          <div className="px-6 pt-8 pb-12">
            <Section title="Monosign">
              <MobileLink href="/sign" onClick={onClose}>{t('Overview')}</MobileLink>
              <MobileLink href="/sign/sso" onClick={onClose}>{t('Single Sign-On')}</MobileLink>
              <MobileLink href="/sign/mfa" onClick={onClose}>{t('Multi-Factor Authentication')}</MobileLink>
              <MobileLink href="/sign/authenticator" onClick={onClose}>{t('Monofor Identity')}</MobileLink>
              <MobileLink href="/sign/directory" onClick={onClose}>{t('Directory & sources')}</MobileLink>
              <MobileLink href="/sign/lifecycle" onClick={onClose}>{t('User lifecycle')}</MobileLink>
              <MobileLink href="/sign/automation" onClick={onClose}>{t('Identity automation')}</MobileLink>
              <MobileLink href="/sign/itdr" onClick={onClose}>{t('ITDR')}</MobileLink>
              <MobileLink href="/sign/ai-agents" onClick={onClose}>{t('AI agent identity')}</MobileLink>
              <MobileLink href="/sign/reporting" onClick={onClose}>{t('Reporting')}</MobileLink>
              <MobileLink href="/sign/security-score" onClick={onClose}>{t('Security Score')}</MobileLink>
              <MobileLink href="/sign/identity-intelligence" onClick={onClose}>{t('Identity intelligence')}</MobileLink>
              <MobileLink href="/sign/mcp-gateway" onClick={onClose}>{t('MCP Gateway')}</MobileLink>
            </Section>

            <Section title="Monopam">
              <MobileLink href="/pam" onClick={onClose}>{t('Overview')}</MobileLink>
              <MobileLink href="/pam/gateway" onClick={onClose}>{t('Gateway')}</MobileLink>
              <MobileLink href="/pam/vault" onClick={onClose}>{t('Credential vault')}</MobileLink>
              <MobileLink href="/pam/session-recording" onClick={onClose}>{t('Session recording')}</MobileLink>
              <MobileLink href="/pam/jit-access" onClick={onClose}>{t('Just-in-time access')}</MobileLink>
              <MobileLink href="/pam/vendor-access" onClick={onClose}>{t('Vendor access (VPAM)')}</MobileLink>
              <MobileLink href="/pam/certificate-manager" onClick={onClose}>{t('Certificate Manager')}</MobileLink>
              <MobileLink href="/pam/kubernetes" onClick={onClose}>{t('Kubernetes & OpenShift')}</MobileLink>
              <MobileLink href="/pam/database-access" onClick={onClose}>{t('Database access')}</MobileLink>
              <MobileLink href="/pam/epm" onClick={onClose}>{t('Endpoint Privilege Management')}</MobileLink>
              <MobileLink href="/pam/secrets-management" onClick={onClose}>{t('Secrets management')}</MobileLink>
              <MobileLink href="/pam/linux" onClick={onClose}>{t('Linux identity agent')}</MobileLink>
              <MobileLink href="/pam/ad-bridging" onClick={onClose}>{t('AD bridging')}</MobileLink>
            </Section>

            <Section title="Monosync">
              <MobileLink href="/sync" onClick={onClose}>{t('Overview')}</MobileLink>
              <MobileLink href="/sync/connectors" onClick={onClose}>{t('Connectors')}</MobileLink>
              <MobileLink href="/sync/api" onClick={onClose}>{t('API')}</MobileLink>
              <MobileLink href="/sync/governance" onClick={onClose}>{t('Governance')}</MobileLink>
            </Section>

            <Section title={t('Solutions')}>
              <MobileLink href="/solutions/ai-security" onClick={onClose}>{t('AI agent security')}</MobileLink>
              <MobileLink href="/solutions/machine-identity" onClick={onClose}>{t('Machine identity')}</MobileLink>
              <MobileLink href="/solutions/identity-security-posture" onClick={onClose}>{t('Identity security posture')}</MobileLink>
              <MobileLink href="/solutions/devops-privileged-access" onClick={onClose}>{t('DevOps privileged access')}</MobileLink>
              {lang === 'tr' && (
                <>
                  <MobileLink href="/solutions/yerli-kimlik-yonetimi" onClick={onClose}>{t('Yerli kimlik yönetimi')}</MobileLink>
                  <MobileLink href="/solutions/kamu-kimlik-yonetimi" onClick={onClose}>{t('Kamu için kimlik yönetimi')}</MobileLink>
                  <MobileLink href="/solutions/bddk-erisim-yonetimi" onClick={onClose}>{t('BDDK denetimine hazır erişim')}</MobileLink>
                </>
              )}
              <MobileLink href="/solutions/zero-trust" onClick={onClose}>{t('Zero Trust')}</MobileLink>
              <MobileLink href="/solutions/joiner-mover-leaver" onClick={onClose}>{t('Joiner / Mover / Leaver')}</MobileLink>
              <MobileLink href="/solutions/passwordless-mfa" onClick={onClose}>{t('Passwordless & MFA')}</MobileLink>
              <MobileLink href="/solutions/audit-compliance" onClick={onClose}>{t('Audit & compliance')}</MobileLink>
              <MobileLink href="/solutions/external-partners" onClick={onClose}>{t('External & partner access')}</MobileLink>
              <MobileLink href="/compliance/kvkk" onClick={onClose}>{t('KVKK compliance')}</MobileLink>
              <MobileLink href="/compliance/dora" onClick={onClose}>{t('DORA compliance')}</MobileLink>
              <MobileLink href="/compliance/gdpr" onClick={onClose}>{t('GDPR compliance')}</MobileLink>
              <MobileLink href="/compliance/nis2" onClick={onClose}>{t('NIS2 compliance')}</MobileLink>
              <MobileLink href="/compliance/iec-62443" onClick={onClose}>{t('IEC 62443 compliance')}</MobileLink>
              {lang === 'tr' && (
                <MobileLink href="/compliance/siber-guvenlik-kanunu" onClick={onClose}>{t('7545 Siber Güvenlik Kanunu')}</MobileLink>
              )}
            </Section>

            <Section title={t('Integrations')}>
              <MobileLink href="/integrations" onClick={onClose}>{t('Browse the catalog')}</MobileLink>
            </Section>

            <Section title={t('Pricing')}>
              <MobileLink href="/pricing" onClick={onClose}>{t('Plans & pricing')}</MobileLink>
            </Section>

            <Section title={t('For you')}>
              <MobileLink href="/for/it-admins" onClick={onClose}>{t('IT admins')}</MobileLink>
              <MobileLink href="/for/security-engineers" onClick={onClose}>{t('Security engineers')}</MobileLink>
              <MobileLink href="/for/developers" onClick={onClose}>{t('Developers')}</MobileLink>
              <MobileLink href="/for/compliance-officers" onClick={onClose}>{t('Compliance officers')}</MobileLink>
              <MobileLink href="/for/end-users" onClick={onClose}>{t('End users')}</MobileLink>
            </Section>

            <Section title={t('Learn')}>
              <MobileLink href="/learn" onClick={onClose}>{t('All how-tos')}</MobileLink>
              <MobileLink href="/learn/sso-aws-saml" onClick={onClose}>{t('SSO into AWS')}</MobileLink>
              <MobileLink href="/learn/mfa-on-vpn" onClick={onClose}>{t('MFA on VPN')}</MobileLink>
              <MobileLink href="/learn/windows-passwordless-login" onClick={onClose}>{t('Windows passwordless')}</MobileLink>
              <MobileLink href="/learn/rest-api-getting-started" onClick={onClose}>{t('REST API getting started')}</MobileLink>
              <MobileLink href="/learn/active-directory-sync" onClick={onClose}>{t('Active Directory sync')}</MobileLink>
              <MobileLink href="/learn/audit-log-siem-export" onClick={onClose}>{t('Audit logs → SIEM')}</MobileLink>
              <MobileLink href="/learn/migrate-from-adfs" onClick={onClose}>{t('Migrate from ADFS')}</MobileLink>
              <MobileLink href="/learn/migrate-from-okta" onClick={onClose}>{t('Migrate from Okta')}</MobileLink>
            </Section>

            <Section title={t('Industries')}>
              <MobileLink href="/solutions/industries/financial-services" onClick={onClose}>{t('Financial Services')}</MobileLink>
              <MobileLink href="/solutions/industries/healthcare" onClick={onClose}>{t('Healthcare')}</MobileLink>
              <MobileLink href="/solutions/industries/manufacturing" onClick={onClose}>{t('Manufacturing')}</MobileLink>
              <MobileLink href="/solutions/industries/public-sector" onClick={onClose}>{t('Public Sector')}</MobileLink>
              <MobileLink href="/solutions/industries/retail" onClick={onClose}>{t('Retail')}</MobileLink>
              <MobileLink href="/solutions/industries/telco" onClick={onClose}>{t('Telecommunications')}</MobileLink>
            </Section>

            {features.length > 0 && (
              <Section title={t('Features')}>
                {features.map((f: any) => (
                  <MobileLink
                    key={f.slug}
                    href={'/features/' + f.slug}
                    onClick={onClose}
                  >
                    {f.title}
                  </MobileLink>
                ))}
              </Section>
            )}

            <Section title={t('Resources')}>
              <MobileLink href="/blog" onClick={onClose}>{t('Blog')}</MobileLink>
              <MobileLink href="/glossary" onClick={onClose}>{t('Glossary')}</MobileLink>
              <MobileLink href="/tools/pam-tco-calculator" onClick={onClose}>{t('PAM TCO Calculator')}</MobileLink>
              <MobileLink href="/tools/identity-compliance-checklist" onClick={onClose}>{t('Compliance Checklist')}</MobileLink>
              <MobileLink href="/tools/ad-service-account-scanner" onClick={onClose}>{t('AD Account Scanner')}</MobileLink>
              <MobileExternal href="https://help.monofor.com">
                {t('Documentation')}
              </MobileExternal>
              <MobileLink href="/press-kit" onClick={onClose}>{t('Press kit')}</MobileLink>
            </Section>

            <Section title={t('Company')}>
              <MobileLink href="/company" onClick={onClose}>{t('About')}</MobileLink>
              <MobileLink href="/become-partner" onClick={onClose}>
                {t('Become a Partner')}
              </MobileLink>
              <MobileLink href="/contact" onClick={onClose}>{t('Contact')}</MobileLink>
            </Section>

            <div className="mt-10 flex flex-col gap-3">
              <Button href="/try/now" variant="primary" size="lg" onClick={onClose}>
                {t('Start free trial →')}
              </Button>
              <Button
                href="/become-partner"
                variant="outline"
                size="lg"
                onClick={onClose}
              >
                {t('Become a Partner')}
              </Button>
              <Button href="/contact" variant="ghost" size="lg" onClick={onClose}>
                {t('Talk with our experts')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-line-subtle py-6 first:pt-0">
      <div className="text-eyebrow font-semibold uppercase text-ink-tertiary">
        {title}
      </div>
      <div className="mt-4 flex flex-col">{children}</div>
    </div>
  )
}

function MobileLink({
  href,
  children,
  onClick,
}: {
  href: string
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="-mx-2 rounded-lg px-2 py-3 text-body-lg text-ink-primary transition hover:bg-surface-muted"
    >
      {children}
    </Link>
  )
}

function MobileExternal({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="-mx-2 rounded-lg px-2 py-3 text-body-lg text-ink-primary transition hover:bg-surface-muted"
    >
      {children}
    </a>
  )
}
